import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { QueryKey, UseQueryOptions } from 'react-query';
import type { QuotationSupplierProduct } from './types';
import { quotationSuppliersEndpoint } from './use-quotation-suppliers-api';

// A suggestion row is a normal catalog product plus the BE's 0–100 name-match
// score (>=95 by contract — the endpoint filters below-threshold rows itself).
export interface SuggestedQuotationProduct extends QuotationSupplierProduct {
  matchScore?: number;
}

type TData = { data: SuggestedQuotationProduct[] };
type TError = AxiosError;
type Options = Omit<UseQueryOptions<TData, TError, TData, QueryKey>, 'queryKey' | 'queryFn'>;

// GET .../quotation-suppliers/:supplierId/products/suggested?itemId=... — top 6
// exact-tier name matches (>=95, exact/reordered only — never a mere-coverage
// false positive) that also pass the item's brand/origin/state/spec
// flexibility gate (user-mngt-srv wt-quotation-suggestions). The BE loads the
// item's master product + flexibility itself, so passing itemId (not raw
// name/unit/brand) keeps the FE from ever drifting from what's stored. A 404
// means the BE isn't deployed yet — callers fall back to client-side ranking,
// so don't retry it.
export function useGetSuggestedQuotationProductsApi(
  supplierId: number | null | undefined,
  itemId: number | null | undefined,
  options?: Options
) {
  return useQueryWrapper<TData, TError>(
    [quotationSuppliersEndpoint, supplierId, 'products/suggested', { itemId }],
    async () => {
      const response = await http.get(`${quotationSuppliersEndpoint}/${supplierId}/products/suggested`, {
        params: { itemId },
      });
      return response.data;
    },
    {
      enabled: !!supplierId && !!itemId,
      retry: (failureCount, error) => error.response?.status !== 404 && failureCount < 2,
      ...options,
    }
  );
}
